import React, { useCallback, useMemo } from 'react';
import { FieldArray, getIn } from 'formik';
import type { useFormikContext } from 'formik';
import InputFieldFactory from '../Factory/InputFieldFactory';
import { Box, Button, IconButton, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type { ArrayFieldProps } from './Array';
import { FormLabelStyled } from 'app/components/SecurityInput/SecurityInput.styles';
import { useIntl } from 'react-intl';
import type { Field } from 'app/types';
import { ArrayCard, FullWidth, ItemBox } from './InternalArrayField.styles';

type InternalArrayFieldProps = ArrayFieldProps & {
  formik: ReturnType<typeof useFormikContext>;
};

const InternalArrayField: React.FC<InternalArrayFieldProps> = ({ field, className, hideErrorText, formik }) => {
  const { name, items = [], header } = field;
  const { values } = formik;
  const currentFieldValue = useMemo(() => getIn(values, name) || [], [values, name]);
  const intl = useIntl();

  // Create an empty item based on the field items structure
  const createEmptyItem = useCallback(() => {
    return items.reduce((acc: any, item: any) => {
      // Use fieldName from the API structure
      const fieldName = item.fieldName || item.name;
      acc[fieldName] = item.fieldOptions?.default ?? '';
      return acc;
    }, {});
  }, [items]);

  const handleAdd = useCallback(() => {
    const emptyItem = createEmptyItem();
    const newValue = [...currentFieldValue, emptyItem];
    formik.setFieldValue(name, newValue);
  }, [formik, name, createEmptyItem, currentFieldValue]);

  const handleRemove = useCallback(
    (idx: number) => {
      const newValue = currentFieldValue.filter((_: unknown, index: number) => index !== idx);
      formik.setFieldValue(name, newValue);
    },
    [formik, name, currentFieldValue],
  );

  return (
    <Box className={className}>
      <FieldArray name={name}>
        {() => (
          <ArrayCard>
            {header && <FormLabelStyled sx={{ mb: 2 }}>{intl.formatMessage({ id: header })}</FormLabelStyled>}
            {currentFieldValue.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {currentFieldValue.map((data: unknown, index: number) => (
                  <React.Fragment key={index}>
                    <Divider sx={{ my: 1 }} />

                    <ItemBox>
                      {items.map((item: Field) => {
                        const fieldName = item.fieldName || item.name;
                        const fieldType = item.fieldType || item.type;

                        return (
                          <FullWidth key={fieldName}>
                            <InputFieldFactory
                              field={{
                                name: `${name}.${index}.${fieldName}`,
                                type: fieldType,
                                required: item.fieldOptions?.required || false,
                                header: `${header}.${fieldName}`,
                                label: item.label || item.fieldOptions?.label,
                                placeholder: item.placeholder || item.fieldOptions?.placeholder,
                                options: item.fieldOptions,
                                ...item.fieldOptions,
                              }}
                              hideErrorText={hideErrorText}
                            />
                          </FullWidth>
                        );
                      })}
                      <IconButton onClick={() => handleRemove(index)} size="small" color="error" sx={{ mt: 2 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ItemBox>
                  </React.Fragment>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={handleAdd}
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                sx={{ minWidth: 120 }}
              >
                {intl.formatMessage({ id: 'button.add' })}
              </Button>
            </Box>
          </ArrayCard>
        )}
      </FieldArray>
    </Box>
  );
};

export default InternalArrayField;
